import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

interface CookieCategory {
  id: string;
  names: Record<string, string>;
  descriptions: Record<string, string>;
  required: boolean;
}

const COOKIE_CATEGORIES: CookieCategory[] = [
  {
    id: "essential",
    names: { en: "Essential", he: "חיוניים", ar: "أساسية", ru: "Основные" },
    descriptions: {
      en: "Required for the app to function properly",
      he: "נדרשים לפעולה תקינה של האפליקציה",
      ar: "مطلوبة لعمل التطبيق بشكل صحيح",
      ru: "Необходимы для правильной работы приложения",
    },
    required: true,
  },
  {
    id: "functional",
    names: { en: "Functional", he: "פונקציונליים", ar: "وظيفية", ru: "Функциональные" },
    descriptions: {
      en: "Enable personalization and enhanced features",
      he: "מאפשרים התאמה אישית ותכונות מתקדמות",
      ar: "تتيح التخصيص والميزات المحسّنة",
      ru: "Позволяют персонализацию и расширенные функции",
    },
    required: false,
  },
  {
    id: "analytics",
    names: { en: "Analytics", he: "אנליטיקה", ar: "تحليلات", ru: "Аналитика" },
    descriptions: {
      en: "Help us understand how visitors use the app",
      he: "עוזרים לנו להבין איך מבקרים משתמשים באפליקציה",
      ar: "تساعدنا على فهم كيفية استخدام الزوار للتطبيق",
      ru: "Помогают нам понять, как посетители используют приложение",
    },
    required: false,
  },
];

interface CookieConsent {
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  timestamp: number;
}

const CONSENT_KEY = "cookie-consent";

export const getCookieConsent = (): CookieConsent | null => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Silent fail
  }
  return null;
};

export const saveCookieConsent = (consent: CookieConsent) => {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  } catch (e) {
    // Silent fail
  }
};

export default function CookieSettings() {
  const navigate = useNavigate();
  const { t, lang, dir } = useTranslation();

  const localized = (map: Record<string, string>) => map[lang] || map.en || "";
  const titleMap: Record<string, string> = { he: "הגדרות עוגיות", en: "Cookie Settings", ar: "إعدادات ملفات تعريف الارتباط", ru: "Настройки cookie" };
  const descMap: Record<string, string> = {
    he: "אנחנו משתמשים בעוגיות כדי לשפר את חווית השימוש שלך. בחר את סוגי העוגיות שאתה מסכים להן.",
    en: "We use cookies to improve your experience. Choose which cookies you consent to.",
    ar: "نستخدم ملفات تعريف الارتباط لتحسين تجربتك. اختر أنواع ملفات تعريف الارتباط التي توافق عليها.",
    ru: "Мы используем файлы cookie для улучшения вашего опыта. Выберите, на какие файлы cookie вы согласны.",
  };
  const requiredLabel: Record<string, string> = { he: "חובה", en: "Required", ar: "مطلوب", ru: "Обязательно" };
  const rejectAllLabel: Record<string, string> = { he: "דחה הכל", en: "Reject All", ar: "رفض الكل", ru: "Отклонить все" };
  const saveLabel: Record<string, string> = { he: "שמור בחירה", en: "Save Selection", ar: "حفظ الاختيار", ru: "Сохранить выбор" };
  const acceptAllLabel: Record<string, string> = { he: "אשר הכל", en: "Accept All", ar: "قبول الكل", ru: "Принять все" };

  const [consent, setConsent] = useState<Record<string, boolean>>({
    essential: true,
    functional: true,
    analytics: false,
  });

  useEffect(() => {
    const stored = getCookieConsent();
    if (stored) {
      setConsent({
        essential: stored.essential,
        functional: stored.functional,
        analytics: stored.analytics,
      });
    }
  }, []);

  const handleToggle = (categoryId: string) => {
    if (categoryId === "essential") return;
    setConsent((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleAcceptAll = () => {
    const newConsent: CookieConsent = {
      essential: true,
      functional: true,
      analytics: true,
      timestamp: Date.now(),
    };
    saveCookieConsent(newConsent);
    navigate(-1);
  };

  const handleAcceptSelected = () => {
    const newConsent: CookieConsent = {
      essential: true,
      functional: consent.functional,
      analytics: consent.analytics,
      timestamp: Date.now(),
    };
    saveCookieConsent(newConsent);
    navigate(-1);
  };

  const handleRejectAll = () => {
    const newConsent: CookieConsent = {
      essential: true,
      functional: false,
      analytics: false,
      timestamp: Date.now(),
    };
    saveCookieConsent(newConsent);
    navigate(-1);
  };

  return (
    <>
      <Helmet>
        <title>{localized(titleMap)} - My EyeControl</title>
      </Helmet>

      <MainLayout>
        <div className="flex-1 flex flex-col h-full overflow-auto" dir={dir}>
          {/* Header */}
          <div className="flex items-center gap-4 p-6 border-b border-border">
            <Cookie className="w-8 h-8 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">
              {localized(titleMap)}
            </h1>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 space-y-6 max-w-2xl mx-auto w-full">
            <p className="text-muted-foreground">
              {localized(descMap)}
            </p>

            {/* Toggles */}
            <div className="space-y-4 rounded-xl p-2">
              {COOKIE_CATEGORIES.filter(c => !c.required).map((category, idx) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border cursor-pointer"
                  data-scan-item
                  onClick={() => handleToggle(category.id)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground text-lg">
                        {localized(category.names)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {localized(category.descriptions)}
                    </p>
                  </div>
                  <Switch
                    checked={consent[category.id]}
                    onCheckedChange={() => handleToggle(category.id)}
                  />
                </div>
              ))}
              {/* Essential always shown but not scannable */}
              {COOKIE_CATEGORIES.filter(c => c.required).map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border opacity-60"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground text-lg">
                        {localized(category.names)}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary">
                        {localized(requiredLabel)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {localized(category.descriptions)}
                    </p>
                  </div>
                  <Switch checked disabled />
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 rounded-xl p-2">
              <Button
                variant="outline"
                onClick={handleRejectAll}
                className="flex-1 h-14 text-lg"
                data-scan-item
              >
                {localized(rejectAllLabel)}
              </Button>
              <Button
                variant="outline"
                onClick={handleAcceptSelected}
                className="flex-1 h-14 text-lg"
                data-scan-item
              >
                {localized(saveLabel)}
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="flex-1 h-14 text-lg"
                data-scan-item
              >
                {localized(acceptAllLabel)}
              </Button>
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
}
